import React, { useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel, FormHelperText } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Form, FormikErrors, FormikTouched } from "formik";
import dayjs from 'dayjs';
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import { BeaconDialogModes } from "./BeaconDialog";
import { addMonths } from "date-fns";

interface BeaconScheduleFormProps {
  values: MakeBeacon,
  errors: FormikErrors<MakeBeacon>,
  touched: FormikTouched<MakeBeacon>,
  handleChange: (e: React.ChangeEvent<any>) => void,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<MakeBeacon>>
  mode?: typeof BeaconDialogModes[number]
}

const BeaconScheduleForm: React.FC<BeaconScheduleFormProps> = ({ values, errors, touched, handleChange, setFieldValue, mode }) => {

  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs(new Date()));
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs(new Date()));
  const [isAlwaysActive, setIsAlwaysActive] = useState<boolean>(values.startDate == "" && values.endDate == "");

  useEffect(() => {
    if (values.startDate && !isAlwaysActive) {
      setStartDate(dayjs(new Date(values.startDate)));
    }
    else {
      setFieldValue("startDate", "");
    }

    if (values.endDate && !isAlwaysActive) {
      setEndDate(dayjs(new Date(values.endDate)));
    }
    else {
      setFieldValue("endDate", "");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsAlwaysActive(checked);

    if (checked) {
      setFieldValue("startDate", "");
      setFieldValue("endDate", "");
    } else {
      const newStart = new Date();
      const newEnd = addMonths(newStart, 1);

      setFieldValue("startDate", newStart);
      setFieldValue("endDate", newEnd);

      setStartDate(dayjs(newStart));
      setEndDate(dayjs(newEnd));
    }
    handleChange(event);
  };

  return (
    <Form style={{ width: "100%", marginTop: 8 }}>
      <Box sx={{ p: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* Grid for DT Pick Controls */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ width: "25%" }}>
              <DateTimePicker
                disabled={isAlwaysActive || mode === "MOD_ONLY" || mode === "READ_ONLY"}
                sx={{ width: "100%" }}
                label="Start time"
                value={startDate}
                onChange={(value) => setFieldValue("startDate", value)}
                closeOnSelect={false}
              />
              <FormHelperText
                error={
                  (errors.startDate ? true : undefined) && touched.startDate
                }
              >
                {errors.startDate && touched.startDate
                  ? errors.startDate
                  : null}
              </FormHelperText>
            </Box>
            <Box sx={{ width: "25%"}}>
              <DateTimePicker
                disabled={isAlwaysActive || mode === "MOD_ONLY" || mode === "READ_ONLY"}
                sx={{ width: "100%" }}
                label="End time"
                value={endDate}
                onChange={(value) => setFieldValue("endDate", value)}
                closeOnSelect={false}
              />
              <FormHelperText
                error={(errors.endDate ? true : undefined) && touched.endDate}
              >
                {errors.endDate && touched.endDate ? errors.endDate : null}
              </FormHelperText>
            </Box>
            <Box sx={{ display: "flex", alignContent: "center", ml: 6 }}>
              <FormControlLabel control={<Checkbox checked={isAlwaysActive} onChange={handleCheckboxChange}/>} label="Always Active" />
            </Box>
          </Box>
        </LocalizationProvider>
      </Box>
    </Form>
  );
};

export default BeaconScheduleForm;
